import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { categoryLabel } from "@/lib/blog";
import { ArrowLeft, LogOut, ExternalLink, Eye } from "lucide-react";
import { toast } from "sonner";

interface StatsRow {
  post_id: string;
  title: string;
  slug: string;
  category: string;
  total: number;
  de_views: number;
  en_views: number;
  last_view: string | null;
}

type RangeKey = "7" | "30" | "90" | "all";

const ranges: { value: RangeKey; label: string; since: string | null }[] = [
  { value: "7", label: "Letzte 7 Tage", since: "-7 days" },
  { value: "30", label: "Letzte 30 Tage", since: "-30 days" },
  { value: "90", label: "Letzte 90 Tage", since: "-90 days" },
  { value: "all", label: "Gesamt", since: null },
];

const AdminBlogStats = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<StatsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<RangeKey>("30");

  const since = useMemo(() => {
    const r = ranges.find((x) => x.value === range);
    if (!r?.since) return null;
    const d = new Date();
    d.setDate(d.getDate() - parseInt(r.since.replace("-", "").replace(" days", ""), 10));
    return d.toISOString();
  }, [range]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("blog_view_stats", {
      _since: since,
    });
    if (error) {
      toast.error(error.message);
      setRows([]);
    } else {
      setRows((data as StatsRow[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [since]);

  const logout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  const totalViews = rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0);
  const topPost = rows[0];

  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-background border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Magazin-Statistik</h1>
            <p className="text-xs text-muted-foreground">Praxis Innere Balance</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/blog"><ArrowLeft className="w-4 h-4 mr-2" />Zurück zur Liste</Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ranges.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-muted-foreground">
            Zählung startet ab heute. Keine rückwirkenden Daten.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Card className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Aufrufe im Zeitraum</p>
            <p className="text-3xl font-bold text-foreground">{totalViews.toLocaleString("de-AT")}</p>
          </Card>
          <Card className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Meistgelesener Beitrag</p>
            <p className="text-lg font-semibold text-foreground line-clamp-2">
              {topPost ? topPost.title : "—"}
            </p>
            {topPost && (
              <p className="text-xs text-muted-foreground mt-1">
                {Number(topPost.total).toLocaleString("de-AT")} Aufrufe
              </p>
            )}
          </Card>
          <Card className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Beiträge erfasst</p>
            <p className="text-3xl font-bold text-foreground">{rows.length}</p>
          </Card>
        </div>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Beitrag</TableHead>
                <TableHead className="w-32">Kategorie</TableHead>
                <TableHead className="text-right w-28">Aufrufe</TableHead>
                <TableHead className="text-right w-24">DE</TableHead>
                <TableHead className="text-right w-24">EN</TableHead>
                <TableHead className="w-40">Letzter Aufruf</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Lade Statistik…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Noch keine Aufrufe erfasst.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.post_id}>
                    <TableCell>
                      <div className="font-medium text-foreground">{row.title}</div>
                      <div className="text-xs text-muted-foreground">/{row.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        {categoryLabel(row.category)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {Number(row.total).toLocaleString("de-AT")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {Number(row.de_views).toLocaleString("de-AT")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {Number(row.en_views).toLocaleString("de-AT")}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(row.last_view)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" asChild className="h-8 w-8 p-0">
                        <Link to={`/blog/${row.slug}`} target="_blank">
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        <div className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
          <Eye className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Es werden nur anonyme Seitenaufrufe gezählt (Beitrags-ID, Sprache, Zeitpunkt). Keine IP-Adresse,
            keine Cookies, keine personenbezogenen Daten. Pro Browser-Sitzung wird ein Beitrag maximal einmal
            gezählt. Aufrufe aus dem Adminbereich werden nicht erfasst.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AdminBlogStats;
